import { TrashIcon } from "@/assets/icons";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useShoppingCart } from "use-shopping-cart";
import { formatPrice } from "@/utils/formatePrice";

type CartAddon = {
  id: string;
  name: string;
  price: number;
};

type CartItemProps = {
  item: {
    id: string;
    name: string;
    price: number;
    basePrice?: number;
    originalBasePrice?: number;
    discountLabel?: string;
    quantity: number;
    image?: string;
    addons?: CartAddon[];
    options?: string[];
  };
  toggle: () => void;
};

const SingleItem = ({ item, toggle }: CartItemProps) => {
  const { removeItem } = useShoppingCart();
  const t = useTranslations("");

  const handleRemoveFromCart = () => {
    removeItem(item.id);
  };

  const imageSrc =
    typeof item.image === "string" && item.image.trim().length > 0
      ? item.image
      : "/images/placeholder.png";

  return (
    <div className="flex items-center justify-between gap-5">
      <div className="flex items-start w-full gap-6">
        <div className="flex items-center justify-center rounded-[10px] bg-gray-3 max-w-[90px] w-full h-22.5">
          <Image
            src={imageSrc}
            alt="product"
            width={100}
            height={100}
            sizes="90px"
          />
        </div>

        <div className="flex-1">
          <h3 className="mb-1 font-medium duration-200 ease-out text-dark ">
            {item.name} x{item.quantity}
            {item.discountLabel && (
              <span className="ml-2 text-white text-[0.6rem] font-bold tracking-widest uppercase px-2 py-0.5 rounded bg-red align-middle">
                {item.discountLabel}
              </span>
            )}
          </h3>
          <p className="text-custom-sm">
            {t("CartView.priceLabel")}{" "}
            {item.originalBasePrice ? (
              <>
                <span className="text-dark-4 line-through">
                  {formatPrice(item.originalBasePrice)}
                </span>{" "}
                <span className="text-red">
                  {formatPrice(item.basePrice ?? item.price)}
                </span>
              </>
            ) : (
              formatPrice(item.basePrice ?? item.price)
            )}
          </p>
          {item.options && item.options.length > 0 && (
            <div className="mt-1 space-y-1">
              <p className="text-xs font-medium text-gray-600">
                {t("Addons.productOptions")}
              </p>
              {item.options.map((option, index) => (
                <p key={index} className="text-xs text-gray-500">
                  {option}
                </p>
              ))}
            </div>
          )}
          {item.addons && item.addons.length > 0 && (
            <div className="mt-2 space-y-1">
              <p className="text-xs font-medium text-gray-600">
                {t("Addons.title")}
              </p>
              {item.addons.map((addon, index) => (
                <p key={`${addon.id}-${index}`} className="text-xs text-gray-500">
                  + {addon.name} ({formatPrice(addon.price)})
                </p>
              ))}
            </div>
          )}
        </div>
      </div>

      <button
        onClick={handleRemoveFromCart}
        aria-label="button for remove product from cart"
        className="flex items-center justify-center rounded-full max-w-[38px] w-full h-9.5 bg-gray-2 border border-gray-3 text-dark ease-out duration-200 hover:bg-red-light-6 hover:border-red-light-4 hover:text-red"
      >
        <TrashIcon className="w-5 h-5" />
      </button>
    </div>
  );
};

export default SingleItem;
